import { useState, useEffect } from 'react'
import { setUser as setSentryUser } from '@sentry/react'
import { useAnalytics } from '@contexts/analytics'
import { useCurrentUser } from '@contexts/currentUser'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { client } from '@helpers/api'

import FlashMessage, { useFlashMessage } from '@components/FlashMessage'
import BasicLayout from '@/components/Onboarding/Layout/BasicLayout'
import { Button } from '@/components/ui/button'
import Spinner from '@components/Spinner'

const EmailConfirmation = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const { identify } = useAnalytics()
  const navigate = useNavigate()
  const { user: { signedIn }, setUser } = useCurrentUser()

  const { setFlashMessage } = useFlashMessage()

  const email = searchParams.get('email')
  const sent = searchParams.get('sent')
  const confirmationToken = searchParams.get('confirmation_token')

  const signOut = () => navigate('/sign_in')

  const { mutate: sendConfirmationEmail, isLoading: isSendingConfirmationEmail } = useMutation({
    mutationFn: async () => await client.post('/users/confirmation/resend', { email }),
    onSuccess: response => {
      setSearchParams({ email, sent: true })
      setFlashMessage(`Confirmation email sent to ${email}`, 'success')
    },
    onError: error => {
      setFlashMessage('There was an error sending the confirmation email', 'error')
    }
  })

  const { mutate: confirmEmail, isLoading: isConfirmingEmail } = useMutation({
    mutationFn: async () => await client.get(`/users/confirmation?confirmation_token=${confirmationToken}`),
    onSuccess: response => {
      setUser({ ...response.data })
      setSentryUser({ id: response.data.id, email: response.data.email })
      identify(response.data.id)

      if (response.data.role === 'student') {
        navigate('/students/onboarding')
      } else {
        navigate('/educators')
      }
    },
    onError: error => {
      setFlashMessage('There was an error confirming your email', 'error')
      navigate('/sign_in')
    }
  })

  useEffect(() => {
    if (confirmationToken) confirmEmail()
  }, [confirmationToken])

  return (
    <BasicLayout>
      <FlashMessage />

      <h1 className='text-2xl font-extrabold tracking-tight mb-4'>
        Verify your email
      </h1>
      <Choose>
        <When condition={isConfirmingEmail}>
          <Spinner />
        </When>
        <When condition={sent}>
          <p>
            We've sent a verification link to <strong className='whitespace-nowrap'>{email}</strong>. Please click it to confirm your email address.
          </p>
          <div className='flex gap-2 mt-6'>
            <Button variant='outline' onClick={sendConfirmationEmail} loading={isSendingConfirmationEmail}>
              Resend email
            </Button>
            <Button variant='outline' onClick={signOut}>
              Sign out
            </Button>
          </div>
        </When>
        <Otherwise>
          <p>We'll send you a verification link to <strong>{email}</strong>.</p>
          <div className='flex gap-2 mt-6'>
            <Button onClick={sendConfirmationEmail} loading={isSendingConfirmationEmail}>
              Send email
            </Button>
            <Button variant='outline' onClick={signOut}>
              Sign out
            </Button>
          </div>
        </Otherwise>
      </Choose>
    </BasicLayout>
  )
}

export default EmailConfirmation
